import React, { useMemo } from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import {
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaCorner,
  ScrollMask,
} from './ScrollArea.styled';

export const SCROLL_MASK_PLACEMENT = {
  Top: 'top',
  Bottom: 'bottom',
  Left: 'left',
  Right: 'right',
} as const;

export type ScrollMaskPlacement =
  (typeof SCROLL_MASK_PLACEMENT)[keyof typeof SCROLL_MASK_PLACEMENT];

export type ScrollAreaProps = {
  /**
   * The content that should be scrollable
   */
  children: React.ReactNode;
  /**
   * The type of scrollbar that should be rendered
   */
  type?: ScrollAreaPrimitive.ScrollAreaProps['type'];
  /**
   * The amount of time in milliseconds before the scrollbar hides when using type="auto"
   */
  scrollHideDelay?: number;
  /**
   * The className to be applied to the root element
   */
  className?: string;
  /**
   * Whether to show the horizontal scrollbar
   */
  showHorizontal?: boolean;
  /**
   * Whether to show the vertical scrollbar (default: true)
   */
  showVertical?: boolean;
  /**
   * The height of the scroll area
   */
  height?: string;
  /**
   * The width of the scroll area
   */
  width?: string;
  /**
   * Scroll mask placements to show. Can be a single placement or an array of placements.
   */
  scrollMasks?: ScrollMaskPlacement | ScrollMaskPlacement[];
  /**
   * Size of the scroll masks (e.g. '20px', '2rem')
   */
  scrollMaskSize?: string;
};

/**
 * ScrollArea component that provides custom scrollbars consistent with the design system
 */
export const ScrollArea = ({
  children,
  type = 'hover',
  scrollHideDelay = 600,
  className,
  showHorizontal = false,
  showVertical = true,
  height,
  width,
  scrollMasks = [],
  scrollMaskSize = '40px',
}: ScrollAreaProps): JSX.Element => {
  const { showTopMask, showBottomMask, showLeftMask, showRightMask } = useMemo(() => {
    const maskArray = Array.isArray(scrollMasks) ? scrollMasks : [scrollMasks];

    return {
      showTopMask: maskArray.includes(SCROLL_MASK_PLACEMENT.Top),
      showBottomMask: maskArray.includes(SCROLL_MASK_PLACEMENT.Bottom),
      showLeftMask: maskArray.includes(SCROLL_MASK_PLACEMENT.Left),
      showRightMask: maskArray.includes(SCROLL_MASK_PLACEMENT.Right),
    };
  }, [scrollMasks]);

  return (
    <ScrollAreaRoot
      type={type}
      scrollHideDelay={scrollHideDelay}
      className={className}
      style={{
        height: height || '100%',
        width: width || '100%',
      }}
      data-test="scroll-area"
    >
      {showTopMask && (
        <ScrollMask position="top" size={scrollMaskSize} data-test="scroll-area-mask-top" />
      )}

      {showBottomMask && (
        <ScrollMask position="bottom" size={scrollMaskSize} data-test="scroll-area-mask-bottom" />
      )}

      {showLeftMask && (
        <ScrollMask position="left" size={scrollMaskSize} data-test="scroll-area-mask-left" />
      )}

      {showRightMask && (
        <ScrollMask position="right" size={scrollMaskSize} data-test="scroll-area-mask-right" />
      )}

      <ScrollAreaViewport data-test="scroll-area-viewport">{children}</ScrollAreaViewport>

      {showVertical && (
        <ScrollAreaScrollbar orientation="vertical" data-test="scroll-area-scrollbar-vertical">
          <ScrollAreaThumb data-test="scroll-area-thumb-vertical" />
        </ScrollAreaScrollbar>
      )}

      {showHorizontal && (
        <ScrollAreaScrollbar orientation="horizontal" data-test="scroll-area-scrollbar-horizontal">
          <ScrollAreaThumb data-test="scroll-area-thumb-horizontal" />
        </ScrollAreaScrollbar>
      )}

      {showHorizontal && showVertical && <ScrollAreaCorner data-test="scroll-area-corner" />}
    </ScrollAreaRoot>
  );
};
